import { AutocompleteWithLabel, InputWithLabel } from '@adotel/components'
import { useLazyQuery, useQuery } from '@apollo/client'
import {
  Button,
  ButtonBase,
  FormControlLabel,
  Grow,
  Radio,
  RadioGroup,
  Typography
} from '@material-ui/core'
import { useEffect, useRef, useState } from 'react'

import TimePeriodForm from '../../components/TimePeriodForm'
import { ACCOMMODATIONS_QUERY } from '../../graphql/accommodation'
import { CHANNELS_QUERY } from '../../graphql/channel'
import { PROVIDERS_QUERY } from '../../graphql/providerQueries'
import { RATEPLANS_QUERY } from '../../graphql/rateplan'
import { ROOMTYPES_QUERY } from '../../graphql/roomtype'
import { TENANTS_QUERY } from '../../graphql/tenant'
import { DateObject } from '../../helpers/date'
import {
  getCurrenciesAvailable,
  getEntityCode,
  getEntityId,
  getEntityName,
  getRoleFromStorage
} from '../../helpers/userData'
import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import bookRoleManagement from '../../staticRoleManagement/bookRoleManagement'
import { ROLE } from '../../staticRoleManagement/types'
import { Accommodation } from '../../types/accommodation'
import { BookingListInput, URlFiltersData } from '../../types/booking'
import { Channel } from '../../types/channel'
import { ProviderBrief } from '../../types/provider'
import { RatePlanBrief } from '../../types/rateplan'
import { RoomTypeBrief } from '../../types/roomtype'
import { Tenant } from '../../types/tenants'
import {
  CRSStatusOptions,
  PMSStatusOptions
} from './BookingListFilter/CRSAndPMSStatusOptions'
import useStyles from './styles/BookingListFilter.style'

interface BookingListFiltersProps {
  bookingListFiltersValues: BookingListInput
  setBookingListFiltersValues: React.Dispatch<
    React.SetStateAction<BookingListInput>
  >
  setUrlFiltersData: React.Dispatch<React.SetStateAction<URlFiltersData>>
  urlFiltersData: URlFiltersData
  onFindClick: () => void
  setPage: React.Dispatch<React.SetStateAction<number>>
  perPage: number
  loadingForGetBookingList: boolean
}

const BookingListFilters: React.FC<BookingListFiltersProps> = ({
  setUrlFiltersData,
  urlFiltersData,
  onFindClick,
  // perPage,
  setPage,
  loadingForGetBookingList
}) => {
  const classes = useStyles()
  const [arrivalAndDepartureSelected, setArrivalAndDepartureSelected] =
    useState<boolean>(false)
  const [reserverdDatesSelected, setReserverdDatesSelected] =
    useState<boolean>(false)
  const [departureDateSelected, setDepartureDateSelected] =
    useState<boolean>(false)
  const [includeDate, setincludeDate] = useState<boolean>(false)
  const [onDoneClickForCloseDatePicker, setOnDoneClickForCloseDatePicker] =
    useState<boolean>(true)
  const [roomtypeOptions, setroomtypeOptions] = useState<RoomTypeBrief[]>([])
  const [rateplanOptions, setrateplanOptions] = useState<RatePlanBrief[]>([])
  const [autoCompleteForTenants, setAutoCompleteForTenants] = useState<any[]>(
    getRoleFromStorage().includes('TENANT')
      ? [
          {
            label: getEntityName(),
            value: getEntityCode(),
            code: getEntityCode()
          }
        ]
      : urlFiltersData.tenantsCode.map((item) => ({
          label: item.label,
          value: item.value.toString()
        }))
  )

  useEffect(() => {
    setAutoCompleteForTenants(
      getRoleFromStorage().includes('TENANT')
        ? [
            {
              label: getEntityName(),
              value: getEntityCode(),
              code: getEntityCode(),
              id: getEntityId()
            }
          ]
        : urlFiltersData.tenantsCode.map((item) => ({
            label: item.label,
            value: item.value.toString(),
            code: item.code,
            id: item.id
          }))
    )
  }, [getRoleFromStorage()])

  const [autoCompleteForAccommodations, setAutoCompleteForAccommodations] =
    useState<any[]>(
      getRoleFromStorage() === 'ACCOMMODATION'
        ? [
            {
              label: getEntityName(),
              value: getEntityCode(),
              code: getEntityCode()
            }
          ]
        : urlFiltersData.propertyCode.map((item) => ({
            label: item.label,
            value: item.value,
            code: item.code,
            id: item.id
          }))
    )

  const [autoCompleteForRoomType, setAutoCompleteForRoomType] = useState<any[]>(
    urlFiltersData.roomtypeId.map((item) => ({
      label: item.label,
      value: item.value,
      code: item.code,
      id: item.id
    }))
  )

  useEffect(() => {
    setAutoCompleteForRoomType(
      urlFiltersData.roomtypeId.map((item) => ({
        label: item.label,
        value: item.value,
        code: item.code,
        id: item.id
      }))
    )
  }, [roomtypeOptions])

  const [autoCompleteForRatePlan, setAutoCompleteForRatePlan] = useState<any[]>(
    urlFiltersData.rateplanId.map((item) => ({
      label: item.label,
      value: item.value,
      code: item.code,
      id: item.id
    }))
  )

  useEffect(() => {
    setAutoCompleteForRatePlan(
      urlFiltersData.rateplanId.map((item) => ({
        label: item.label,
        value: item.value,
        code: item.code,
        id: item.id
      }))
    )
  }, [rateplanOptions])

  const [autoCompleteForChannels, setAutoCompleteForChannels] = useState<any[]>(
    urlFiltersData.channelsId.map((item) => ({
      label: item.label,
      value: item.value
    }))
  )
  const [autoCompleteForCRSStatuses, setAutoCompleteForCRSStatuses] = useState<
    any[]
  >(
    urlFiltersData.crsStatus.map((item) => ({
      label: item.label,
      value: item.value
    }))
  )
  const [autoCompleteForPMSStatuses, setAutoCompleteForPMSStatuses] = useState<
    any[]
  >(
    urlFiltersData.pmsStatus.map((item) => ({
      label: item.label,
      value: item.value
    }))
  )
  const [autoCompleteForCurrency, setAutoCompleteForCurrency] = useState<any>(
    urlFiltersData.currency || undefined
  )
  const [masterCRSNumber, setMasterCRSNumber] = useState<string>(
    urlFiltersData.masterCRSNumber || ''
  )
  const [roomCRSNumber, setRoomCRSNumber] = useState<string>(
    urlFiltersData.roomCRSNumber || ''
  )
  const [sourceReservationNumber, setSourceReservationNumber] =
    useState<string>(urlFiltersData.sourceReservationNumber || '')
  const [guestName, setGuestName] = useState<string>(urlFiltersData.guestName)
  const [autoCompleteForProviders, setAutoCompleteForProviders] = useState<
    any[]
  >(
    urlFiltersData.providersCode.map((item) => ({
      label: item.label,
      value: item.value
    }))
  )
  const [systemAllocations, setSystemAllocations] = useState<boolean>(
    urlFiltersData.systemAllocations
  )
  const [pmsNumber, setPmsNumber] = useState<string>(urlFiltersData.pmsNumber)
  const [dateObjectArrivalDeparture, setDateObjectArrivalDeparture] =
    useState<DateObject>({
      gregorianStartDate: urlFiltersData.startDate,
      gregorianEndDate: urlFiltersData.endDate
    })
  const [dateObjectReserve, setDateObjectReserve] = useState<DateObject>({
    gregorianStartDate: urlFiltersData.reserveStartDate,
    gregorianEndDate: urlFiltersData.reserveEndDate
  })
  const [dateObjectDeparture, setDateObjectDeparture] = useState<DateObject>({
    gregorianStartDate: urlFiltersData.departureStartDate,
    gregorianEndDate: urlFiltersData.departureStartDate
  })
  const [dateObjectincludeDate, setDateObjectincludeDate] =
    useState<DateObject>({
      gregorianStartDate: urlFiltersData.roomstayInclusionDate
    })

  useEffect(() => {
    setUrlFiltersData({
      ...urlFiltersData,
      tenantsCode: autoCompleteForTenants || [],
      propertyCode: autoCompleteForAccommodations || [],
      roomtypeId: autoCompleteForRoomType || [],
      rateplanId: autoCompleteForRatePlan || [],
      channelsId: autoCompleteForChannels || [],
      crsStatus: autoCompleteForCRSStatuses || [],
      pmsStatus: autoCompleteForPMSStatuses || [],
      currency: autoCompleteForCurrency && autoCompleteForCurrency.value,
      masterCRSNumber: masterCRSNumber || '',
      roomCRSNumber: roomCRSNumber || '',
      sourceReservationNumber: sourceReservationNumber || '',
      pmsNumber: pmsNumber || '',
      startDate: dateObjectArrivalDeparture.gregorianStartDate || '',
      endDate: dateObjectArrivalDeparture.gregorianEndDate || '',
      reserveStartDate: dateObjectReserve.gregorianStartDate || '',
      reserveEndDate: dateObjectReserve.gregorianEndDate || '',
      departureStartDate: dateObjectDeparture.gregorianStartDate || '',
      departureEndDate: dateObjectDeparture.gregorianEndDate || '',
      roomstayInclusionDate: dateObjectincludeDate.gregorianStartDate || '',
      guestName: guestName || '',
      systemAllocations: systemAllocations,
      providersCode: autoCompleteForProviders || []
    })
  }, [
    autoCompleteForTenants,
    autoCompleteForAccommodations,
    autoCompleteForRoomType,
    autoCompleteForRatePlan,
    autoCompleteForChannels,
    autoCompleteForCRSStatuses,
    autoCompleteForProviders,
    autoCompleteForPMSStatuses,
    autoCompleteForCurrency,
    masterCRSNumber,
    roomCRSNumber,
    sourceReservationNumber,
    pmsNumber,
    dateObjectArrivalDeparture,
    dateObjectReserve,
    dateObjectDeparture,
    dateObjectincludeDate,
    guestName,
    systemAllocations,
    autoCompleteForProviders
  ])

  const datePickerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const isArrivalAndDepartureSelected = arrivalAndDepartureSelected
      const isReserverdDatesSelected = reserverdDatesSelected
      const isDepartureDateSelected = departureDateSelected
      const isincludeDateSelected = includeDate

      if (
        datePickerRef.current &&
        !datePickerRef?.current?.contains(event.target as Node)
      ) {
        if (isArrivalAndDepartureSelected) {
          setArrivalAndDepartureSelected(false)
        }
        if (isReserverdDatesSelected) {
          setReserverdDatesSelected(false)
        }
        if (isDepartureDateSelected) {
          setDepartureDateSelected(false)
        }
        if (isincludeDateSelected) {
          setincludeDate(false)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [
    datePickerRef,
    arrivalAndDepartureSelected,
    reserverdDatesSelected,
    departureDateSelected,
    includeDate
  ])

  const hasAccessToChangeTenant = bookRoleManagement
    .find((item) => item.route === '/booking/list')
    ?.sections?.TENANT_SELECTOR.rolesListWithReadAccess?.includes(
      getRoleFromStorage()
    )

  const hasAccessToChangeAccommodation = bookRoleManagement
    .find((item) => item.route === '/booking/list')
    ?.sections?.ACCOMMODATION_SELECTOR.rolesListWithReadAccess?.includes(
      getRoleFromStorage()
    )

  const [getTenants, { data: tenantsList, loading: loadingForTenantsList }] =
    useLazyQuery<{
      tenants: Tenant[]
    }>(TENANTS_QUERY, {
      notifyOnNetworkStatusChange: true
    })

  useEffect(() => {
    if (hasAccessToChangeTenant) {
      getTenants()
    }
  }, [loadingForGetBookingList])

  const { data: accommodationsData, loading: loadingForAccommodation } =
    useQuery<{
      accommodations: Accommodation[]
    }>(ACCOMMODATIONS_QUERY, {
      notifyOnNetworkStatusChange: true
    })

  const [getRoomTypes, { loading: loadingForRoomTypes }] = useLazyQuery<{
    roomTypes: RoomTypeBrief[]
  }>(ROOMTYPES_QUERY, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      setroomtypeOptions(data.roomTypes)
    }
  })

  const [getRatePlans, { loading: loadingForRatePlan }] = useLazyQuery<{
    ratePlans: RatePlanBrief[]
  }>(RATEPLANS_QUERY, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      setrateplanOptions(data.ratePlans)
    }
  })

  const { data: channelsData, loading: loadingForChannels } = useQuery<{
    channels: Channel[]
  }>(CHANNELS_QUERY)

  const { data: providerData, loading: loadingForProvider } = useQuery<{
    providers: ProviderBrief[]
  }>(PROVIDERS_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  useEffect(() => {
    if (
      autoCompleteForAccommodations &&
      autoCompleteForAccommodations.length == 1
    ) {
      getRoomTypes({
        variables: {
          propertyCode: autoCompleteForAccommodations[0].code,
          showDeletedRoomTypes: false,
          showDeactivedRoomTypes: true
        }
      })
    } else if (getRoleFromStorage() === 'ACCOMMODATION') {
      getRoomTypes({
        variables: {
          propertyCode: getEntityCode(),
          showDeletedRoomTypes: false,
          showDeactivedRoomTypes: true
        }
      })
    } else {
      setroomtypeOptions([])
      setrateplanOptions([])
    }
  }, [autoCompleteForAccommodations, getEntityId()])

  useEffect(() => {
    if (
      autoCompleteForAccommodations &&
      autoCompleteForAccommodations.length == 1 &&
      autoCompleteForRoomType &&
      autoCompleteForRoomType.length == 1 &&
      autoCompleteForRoomType[0].code
    ) {
      getRatePlans({
        variables: {
          propertyCode: autoCompleteForAccommodations[0].code,
          roomTypeCode: autoCompleteForRoomType[0].code,
          showDeletedRatePlans: false,
          showDeactivedRatePlans: true
        }
      })
    } else if (
      getRoleFromStorage() === 'ACCOMMODATION' &&
      autoCompleteForRoomType &&
      autoCompleteForRoomType.length === 1 &&
      autoCompleteForRoomType[0].code
    ) {
      getRatePlans({
        variables: {
          propertyCode: getEntityCode(),
          roomTypeCode: autoCompleteForRoomType[0].code,
          showDeletedRatePlans: false,
          showDeactivedRatePlans: true
        }
      })
    } else {
      setrateplanOptions([])
    }
  }, [autoCompleteForAccommodations, autoCompleteForRoomType, getEntityId()])

  useEffect(() => {
    if (getRoleFromStorage() === 'ACCOMMODATION') {
      setAutoCompleteForAccommodations([
        {
          label: accommodationsData?.accommodations[0].basicDetails.name,
          value: getEntityCode(),
          code: getEntityCode(),
          id: getEntityId()
        }
      ])
    }
  }, [accommodationsData?.accommodations[0].basicDetails.name])

  let tenantCodes = autoCompleteForTenants
    ? autoCompleteForTenants.map((t: any) => t.code)
    : []

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        setPage(1)
        onFindClick()
      }
    }

    window.addEventListener('keydown', handleKeyPress)

    return () => {
      window.removeEventListener('keydown', handleKeyPress)
    }
  }, [onFindClick])

  const isRoleAdmin = getRoleFromStorage() === ROLE.ADMIN

  const isChannel = bookRoleManagement
    .find((item) => item.route === '/booking/list')
    ?.sections?.IS_CHANNEL.rolesListWithWriteAccess?.includes(
      getRoleFromStorage()
    )

  useEffect(() => {
    //TODO: uncomment when PHP allow to filter with channelId
    // if (isChannel) {
    //   setAutoCompleteForChannels([
    //     {
    //       label: getEntityName(),
    //       value: getEntityId()
    //     }
    //   ])
    // }
  }, [loadingForChannels])

  return (
    <Grow in>
      <div className={classes.root}>
        <div className={classes.selectBoxes}>
          <div className={classes.boxContainerRow}>
            <AutocompleteWithLabel
              value={autoCompleteForTenants}
              showSelectAll
              multiple
              limitTags={1}
              fullWidth
              getOptionLabel={(option) => option.label || ''}
              label={getTranslate('Tenant')}
              inputSize="small"
              size="small"
              options={
                tenantsList
                  ? tenantsList.tenants.map((item) => ({
                      label: item.basicDetails.name,
                      value: item.basicDetails.tenantCode || '',
                      code: item.basicDetails.displayTenantCode,
                      id: item.basicDetails.tenantId || ''
                    }))
                  : getRoleFromStorage().includes('TENANT')
                  ? [
                      {
                        label: getEntityName() || '',
                        value: getEntityCode() || '',
                        code: getEntityCode() || ''
                      }
                    ]
                  : []
              }
              onChange={(_, v: any) => {
                if (v.length > 0) {
                  const tempSelectedTenants = v.map((item: any) => ({
                    label: item.label,
                    value: item.value,
                    code: item.code,
                    id: item.id
                  }))
                  setAutoCompleteForTenants(tempSelectedTenants)
                } else {
                  setAutoCompleteForTenants([])
                  setAutoCompleteForAccommodations([])
                  setAutoCompleteForRoomType([])
                  setAutoCompleteForRatePlan([])
                }
              }}
              loading={loadingForTenantsList}
              loadingText={getTranslate('Loading') + '...'}
              disabled={!hasAccessToChangeTenant}
            />
            <AutocompleteWithLabel
              value={autoCompleteForAccommodations}
              showSelectAll
              multiple
              limitTags={1}
              fullWidth
              getOptionLabel={(option) => option.label || ''}
              label={getTranslate('Accommodation')}
              inputSize="small"
              size="small"
              options={
                accommodationsData
                  ? accommodationsData?.accommodations
                      .filter((ele) => {
                        return tenantCodes.length > 0
                          ? tenantCodes.includes(
                              ele.basicDetails.displayTenantCode
                            )
                          : true
                      })
                      .map((item) => ({
                        label: item.basicDetails.name,
                        value: item.basicDetails.propertyCode || '',
                        code: item.basicDetails?.propertyCode || '',
                        id: item.basicDetails.accommodationID || ''
                      }))
                  : []
              }
              onChange={(_, v: any) => {
                if (v.length > 0) {
                  const tempSelectedAccs = v.map((item: any) => ({
                    label: item.label,
                    value: item.value,
                    code: item.code,
                    id: item.id
                  }))
                  setAutoCompleteForAccommodations(tempSelectedAccs)
                  setAutoCompleteForRoomType([])
                  setAutoCompleteForRatePlan([])
                } else {
                  setAutoCompleteForAccommodations([])
                  setAutoCompleteForRoomType([])
                  setAutoCompleteForRatePlan([])
                }
              }}
              loading={loadingForAccommodation}
              loadingText={getTranslate('Loading') + '...'}
              disabled={!hasAccessToChangeAccommodation}
            />
            <AutocompleteWithLabel
              value={autoCompleteForRoomType}
              label={getTranslate('Room Type')}
              multiple
              limitTags={1}
              getOptionLabel={(option) => option.label || ''}
              getOptionSelected={(option, value) =>
                option.value === value.value
              }
              options={roomtypeOptions.map((item) => ({
                label: item.name,
                value: item.roomTypeCode || '',
                code: item.roomTypeCode || '',
                id: item.id
              }))}
              onChange={(_, v: any) => {
                if (v.length > 0) {
                  const tempSelectedRoom = v.map((item: any) => ({
                    label: item.label,
                    value: item.value,
                    code: item.code,
                    id: item.id
                  }))
                  setAutoCompleteForRoomType(tempSelectedRoom)
                  setAutoCompleteForRatePlan([])
                } else {
                  setAutoCompleteForRoomType([])
                  setAutoCompleteForRatePlan([])
                }
              }}
              fullWidth
              noHelperText
              inputSize="small"
              size="small"
              loading={loadingForRoomTypes}
              loadingText={getTranslate('Loading') + '...'}
            />
            <AutocompleteWithLabel
              value={autoCompleteForRatePlan}
              multiple
              noHelperText
              fullWidth
              label={getTranslate('Rate Plan')}
              inputSize="small"
              size="small"
              options={rateplanOptions.map((item) => ({
                label: item.name,
                value: item.ratePlanCode || '',
                code: item.ratePlanCode,
                id: item.id || ''
              }))}
              onChange={(_, v: any) => {
                if (v.length > 0) {
                  const tempSelectedrate = v.map((item: any) => ({
                    label: item.label,
                    value: item.value,
                    code: item.code,
                    id: item.id
                  }))
                  setAutoCompleteForRatePlan(tempSelectedrate)
                } else {
                  setAutoCompleteForRatePlan([])
                }
              }}
              loading={loadingForRatePlan}
              loadingText={getTranslate('Loading') + '...'}
            />
          </div>
          <div className={classes.boxContainerRow}>
            <AutocompleteWithLabel
              limitTags={1}
              noHelperText
              multiple
              fullWidth
              disabled={isChannel}
              value={autoCompleteForChannels}
              label={getTranslate('Channel')}
              inputSize="small"
              size="small"
              options={
                channelsData
                  ? channelsData?.channels?.map((item) => ({
                      label: item.basicDetails.name,
                      value: item.basicDetails.channelCode || '',
                      code: item.basicDetails.displayChannelCode,
                      id: item.basicDetails.channelID || ''
                    }))
                  : []
              }
              onChange={(_, v: any) => {
                if (v.length > 0) {
                  const tempSelectedChannels = [
                    ...v.map((item: any) => ({
                      label: item.label,
                      value: item.value,
                      code: item.code,
                      id: item.id
                    }))
                  ]
                  setAutoCompleteForChannels(tempSelectedChannels)
                } else {
                  setAutoCompleteForChannels([])
                }
              }}
              getOptionLabel={(option) => option.label || ''}
              getOptionSelected={(option, value) =>
                option.value === value.value
              }
              loading={loadingForChannels}
              loadingText={getTranslate('Loading') + '...'}
            />
            <AutocompleteWithLabel
              limitTags={1}
              noHelperText
              multiple
              value={autoCompleteForCRSStatuses}
              fullWidth
              label={getTranslate('CRS Status')}
              inputSize="small"
              size="small"
              options={CRSStatusOptions.map((item) => ({
                label: item,
                value: item
              }))}
              onChange={(_, v: any) => {
                if (v.length > 0) {
                  const tempSelectedCRSStatus = [
                    ...v.map((item: any) => ({
                      label: item.label,
                      value: item.value
                    }))
                  ]
                  setAutoCompleteForCRSStatuses(tempSelectedCRSStatus)
                } else {
                  setAutoCompleteForCRSStatuses([])
                }
              }}
              getOptionLabel={(option) => option.label || ''}
              getOptionSelected={(option, value) =>
                option.value === value.value
              }
            />
            <AutocompleteWithLabel
              limitTags={1}
              noHelperText
              multiple
              value={autoCompleteForPMSStatuses}
              fullWidth
              label={getTranslate('PMS Status')}
              inputSize="small"
              size="small"
              options={PMSStatusOptions.map((item) => ({
                label: item,
                value: item
              }))}
              onChange={(_, v: any) => {
                if (v.length > 0) {
                  const tempSelectedPMSStatus = [
                    ...v.map((item: any) => ({
                      label: item.label,
                      value: item.value
                    }))
                  ]
                  setAutoCompleteForPMSStatuses(tempSelectedPMSStatus)
                } else {
                  setAutoCompleteForPMSStatuses([])
                }
              }}
              getOptionLabel={(option) => option.label || ''}
              getOptionSelected={(option, value) =>
                option.value === value.value
              }
            />
            <AutocompleteWithLabel
              noHelperText
              fullWidth
              value={autoCompleteForCurrency ? autoCompleteForCurrency : null}
              label={getTranslate('Currencies')}
              onChange={(_, v: any) => {
                setAutoCompleteForCurrency(v)
              }}
              getOptionLabel={(option) => option?.label || ''}
              getOptionSelected={(option, value) =>
                option.value === value.value
              }
              options={
                getCurrenciesAvailable()
                  ? getCurrenciesAvailable().map((item) => ({
                      label: item as string,
                      value: item as string
                    }))
                  : []
              }
              size="small"
              inputSize="small"
              includeInputInList
              loadingText={getTranslate('Loading') + '...'}
            />
          </div>
          <div
            className={[
              classes.boxContainerRow,
              classes.searchBoxContainerRow
            ].join(' ')}>
            <InputWithLabel
              label={getTranslate('Master CRS Number')}
              autoComplete="off"
              noHelperText
              inputSize="small"
              value={
                masterCRSNumber === undefined ? '' : masterCRSNumber.toString()
              }
              fullWidth
              onChange={(event) => {
                const inputValue = event.currentTarget.value.trim()
                setMasterCRSNumber(inputValue)
              }}
            />
            <InputWithLabel
              label={getTranslate('Room CRS Number')}
              autoComplete="off"
              noHelperText
              inputSize="small"
              value={roomCRSNumber}
              onChange={(event) => {
                const inputValue = event.currentTarget.value.trim()
                setRoomCRSNumber(inputValue)
              }}
              fullWidth
            />
            <InputWithLabel
              label={getTranslate('Source Number')}
              autoComplete="off"
              noHelperText
              inputSize="small"
              value={sourceReservationNumber}
              onChange={(event) =>
                setSourceReservationNumber(event.currentTarget.value)
              }
              fullWidth
            />
            <InputWithLabel
              label={getTranslate('PMS Number')}
              autoComplete="off"
              noHelperText
              inputSize="small"
              value={pmsNumber === undefined ? '' : pmsNumber.toString()}
              onChange={(event) => {
                const inputValue = event.currentTarget.value.trim()
                setPmsNumber(inputValue)
              }}
              fullWidth
            />
          </div>
          <div
            className={[
              classes.boxContainerRow,
              classes.searchBoxContainerRow,
              isRoleAdmin && classes.guestNameSection
            ].join(' ')}>
            <AutocompleteWithLabel
              limitTags={1}
              noHelperText
              multiple
              fullWidth
              value={autoCompleteForProviders}
              label={getTranslate('Provider')}
              inputSize="small"
              size="small"
              options={
                providerData?.providers?.map((provider) => ({
                  label: `${provider.name} - (${provider.displayProviderCode})`,
                  value: provider.providerCode,
                  code: provider.providerCode
                })) || []
              }
              onChange={(event, v: any) => {
                if (v.length > 0) {
                  const tempSelectedProviders = [
                    ...v.map((item: any) => ({
                      label: item.label,
                      value: item.value,
                      code: item.code
                    }))
                  ]

                  setAutoCompleteForProviders(tempSelectedProviders)
                } else {
                  setAutoCompleteForProviders([])
                }
              }}
              getOptionLabel={(option) => option.label || ''}
              getOptionSelected={(option, value) =>
                option.value === value.value && option.label === value.label
              }
              loading={loadingForProvider}
              loadingText={getTranslate('Loading') + '...'}
            />
            <InputWithLabel
              label={getTranslate('Guest Name')}
              autoComplete="off"
              noHelperText
              inputSize="small"
              value={guestName === undefined ? '' : guestName.toString()}
              fullWidth
              onChange={(event) => {
                setGuestName(event.currentTarget.value)
              }}
            />
            {isRoleAdmin && (
              <div className={classes.switchContainer}>
                <Typography>{getTranslate('Reserves Type:')}</Typography>
                <RadioGroup
                  className={classes.switch}
                  value={
                    systemAllocations
                      ? 'System Reserves'
                      : 'Non-System Reserves'
                  }
                  onChange={(event) =>
                    setSystemAllocations(
                      event.target.value === 'Non-System Reserves'
                        ? false
                        : true
                    )
                  }>
                  <FormControlLabel
                    value="Non-System Reserves"
                    control={<Radio color="primary" />}
                    label={getTranslate('Not-System Reserves')}
                  />
                  <FormControlLabel
                    value="System Reserves"
                    control={<Radio color="primary" />}
                    label={getTranslate('System Reserves')}
                  />
                </RadioGroup>
              </div>
            )}
          </div>
        </div>
        <div ref={datePickerRef} className={classes.datePickersContainer}>
          <Typography>{getTranslate('Stay Dates')}</Typography>
          <TimePeriodForm
            isOpen={arrivalAndDepartureSelected}
            setTimePickerSelected={setArrivalAndDepartureSelected}
            dateObject={dateObjectArrivalDeparture}
            onChange={(data: DateObject) => {
              setDateObjectArrivalDeparture(data)
            }}
            onDoneClickForCloseDatePicker={onDoneClickForCloseDatePicker}
            setOnDoneClickForCloseDatePicker={setOnDoneClickForCloseDatePicker}
            enableAllDays
            // enablePastDays
          />
          <Typography>{getTranslate('Reservation Creation Dates')}</Typography>
          <TimePeriodForm
            isOpen={reserverdDatesSelected}
            setTimePickerSelected={setReserverdDatesSelected}
            dateObject={dateObjectReserve}
            onChange={(data: DateObject) => {
              setDateObjectReserve(data)
            }}
            onDoneClickForCloseDatePicker={onDoneClickForCloseDatePicker}
            setOnDoneClickForCloseDatePicker={setOnDoneClickForCloseDatePicker}
            enableAllDays
          />
          <Typography>{getTranslate('Departure Dates')}</Typography>
          <TimePeriodForm
            isOpen={departureDateSelected}
            setTimePickerSelected={setDepartureDateSelected}
            dateObject={dateObjectDeparture}
            onChange={(data: DateObject) => {
              setDateObjectDeparture(data)
            }}
            onDoneClickForCloseDatePicker={onDoneClickForCloseDatePicker}
            setOnDoneClickForCloseDatePicker={setOnDoneClickForCloseDatePicker}
            enableAllDays
          />
          <Typography>{getTranslate('Inclundes Date')}</Typography>
          <TimePeriodForm
            range={false}
            isOpen={includeDate}
            setTimePickerSelected={setincludeDate}
            dateObject={dateObjectincludeDate}
            onChange={(data: DateObject) => {
              setDateObjectincludeDate(data)
            }}
            onDoneClickForCloseDatePicker={onDoneClickForCloseDatePicker}
            setOnDoneClickForCloseDatePicker={setOnDoneClickForCloseDatePicker}
            enableAllDays
          />
          <div className={classes.ButtonsContainer}>
            <Button
              className={classes.resetButton}
              variant="text"
              color="secondary"
              onClick={() => {
                //ToDo
                // setBookingListFiltersValues({})
                if (getRoleFromStorage() === ROLE.ADMIN) {
                  setAutoCompleteForTenants([])
                }
                if (
                  getRoleFromStorage() === ROLE.ADMIN ||
                  getRoleFromStorage().includes('TENANT') ||
                  getRoleFromStorage().includes('CHANNEL')
                ) {
                  setAutoCompleteForAccommodations([])
                }
                setAutoCompleteForRoomType([])
                setAutoCompleteForRatePlan([])
                setAutoCompleteForChannels([])
                setAutoCompleteForCRSStatuses([])
                setAutoCompleteForPMSStatuses([])
                setAutoCompleteForCurrency(undefined)
                setMasterCRSNumber('')
                setRoomCRSNumber('')
                setSourceReservationNumber('')
                setPmsNumber('')
                setDateObjectArrivalDeparture({})
                setDateObjectReserve({})
                setDateObjectDeparture({})
                setDateObjectincludeDate({})
                setGuestName('')
                setSystemAllocations(false)
                setAutoCompleteForProviders([])
              }}>
              {getTranslate('Reset')}
            </Button>
            <ButtonBase
              component={LocalizedLink}
              to={`/booking/list/?filter=${encodeURIComponent(
                JSON.stringify(urlFiltersData)
              )}`}>
              <Button
                className={classes.applyButton}
                variant="contained"
                color="primary"
                disabled={loadingForGetBookingList}
                onClick={() => {
                  setPage(1)
                  onFindClick()
                }}>
                {getTranslate('Apply')}
              </Button>
            </ButtonBase>
          </div>
        </div>
      </div>
    </Grow>
  )
}

export default BookingListFilters
